

import ApiService from './apiService';

class AppStateManager {
  static instance = null;
  static STORAGE_KEY = 'app_state_data';
  static STORAGE_TIMESTAMP_KEY = 'app_state_timestamp';
  static CACHE_DURATION = 60 * 60 * 30 * 1000; // 30 minutes

  constructor() {
    if (AppStateManager.instance) {
      return AppStateManager.instance;
    }

    this.defaultState = {
      days: [],
      periods: [],
      classes: [],
      loading: {
        days: false,
        periods: false,
        classes: false,
      },
      errors: {},
      initialized: false,
      lastUpdated: null,
      schoolId: null,
    };

    this.activeSchoolId = this.getCurrentSchoolId();
    this.state = { ...this.loadPersistedState(), schoolId: this.activeSchoolId };
    this.listeners = new Set();
    this.api = ApiService.getInstance();
    this.handleBrowserRefresh();
    AppStateManager.instance = this;
  }

  static getInstance() {
    if (!AppStateManager.instance) {
      AppStateManager.instance = new AppStateManager();
    }
    return AppStateManager.instance;
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  loadPersistedState() {
    try {
      const savedState = localStorage.getItem(AppStateManager.STORAGE_KEY);
      const timestamp = localStorage.getItem(AppStateManager.STORAGE_TIMESTAMP_KEY);

      if (!savedState || !timestamp) {
        this.clearPersistedState();
        return { ...this.defaultState };
      }

      const parsedState = JSON.parse(savedState);
      if (Object.prototype.hasOwnProperty.call(parsedState, 'classes') || parsedState.schoolId !== this.getCurrentSchoolId()) {
        this.clearPersistedState();
        return { ...this.defaultState };
      }
      const savedTime = parseInt(timestamp, 10);
      const now = Date.now();

      if (now - savedTime > AppStateManager.CACHE_DURATION) {
        this.clearPersistedState();
        return { ...this.defaultState };
      }

      return {
        ...this.defaultState,
        ...parsedState,
        loading: { ...this.defaultState.loading }, // always reset loading on reload
        lastUpdated: savedTime,
      };
    } catch (error) {
      console.error('Error loading persisted state:', error);
      this.clearPersistedState();
      return { ...this.defaultState };
    }
  }

  saveStateToStorage() {
    try {
      // Don't cache classes — filter params (active/inactive/all) make cached data unreliable
      const stateToSave = {
        days: this.state.days,
        periods: this.state.periods,
        initialized: this.state.initialized,
        schoolId: this.activeSchoolId,
      };

      localStorage.setItem(AppStateManager.STORAGE_KEY, JSON.stringify(stateToSave));
      localStorage.setItem(AppStateManager.STORAGE_TIMESTAMP_KEY, Date.now().toString());
      this.state.lastUpdated = Date.now();
    } catch (error) {
      console.error('Error saving state to localStorage:', error);
      if (error.name === 'QuotaExceededError') {
        this.clearPersistedState();
      }
    }
  }

  clearPersistedState() {
    try {
      localStorage.removeItem(AppStateManager.STORAGE_KEY);
      localStorage.removeItem(AppStateManager.STORAGE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Error clearing persisted state:', error);
    }
  }

  // ── Core state management ──────────────────────────────────────────────────

  getState() {
    return this.state;
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  setState(updates) {
    const previousState = { ...this.state };

    this.state = { ...this.state, ...updates };

    // merge nested objects properly
    if (updates.loading) {
      this.state.loading = { ...this.state.loading, ...updates.loading };
    }
    if (updates.errors) {
      this.state.errors = { ...this.state.errors, ...updates.errors };
    }

    if (this.hasStorableDataChanged(previousState, this.state)) {
      this.saveStateToStorage();
    }

    // notify all subscribers
    this.listeners.forEach(callback => {
      try {
        callback(this.state);
      } catch (error) {
        console.error('Error in state listener:', error);
      }
    });
  }

  // only watches days/periods/initialized — classes are not cached
  hasStorableDataChanged(prev, current) {
    return (
      JSON.stringify(prev.days) !== JSON.stringify(current.days) ||
      JSON.stringify(prev.periods) !== JSON.stringify(current.periods) ||
      prev.initialized !== current.initialized
    );
  }

  // ── Initialization ─────────────────────────────────────────────────────────

  handleBrowserRefresh() {
    const token = localStorage.getItem('token');

    if (token && this.state.initialized) {
      const now = Date.now();
      const lastUpdated = this.state.lastUpdated;

      if (!lastUpdated || (now - lastUpdated) > AppStateManager.CACHE_DURATION) {
        this.refreshAllData();
      }
    }
  }

  async initializeAfterLogin() {
    const token = localStorage.getItem('token');

    if (!token) {
      this.reset();
      return;
    }

    this.setActiveSchoolId(this.getCurrentSchoolId());

    if (this.state.initialized && this.hasRecentData()) {
      // days/periods are fresh — still fetch classes since they're not cached
      await this.fetchClasses();
      return;
    }

    this.setState({ initialized: true });
    await this.fetchAllData();
  }

  hasRecentData() {
    if (!this.state.lastUpdated) return false;
    return (Date.now() - this.state.lastUpdated) < AppStateManager.CACHE_DURATION;
  }

  async fetchAllData() {
    try {
      await Promise.allSettled([
        this.fetchDays(),
        this.fetchPeriods(),
        this.fetchClasses(),
      ]);
    } catch (error) {
      console.error('Error in fetchAllData:', error);
    }
  }

  async refreshAllData() {
    await this.fetchAllData();
  }

  // ── Fetchers ───────────────────────────────────────────────────────────────

  async fetchDays() {
    try {
      this.setState({
        loading: { days: true },
        errors: { days: null }
      });

      const days = await this.api.get('/days');

      this.setState({
        days: days || [],
        loading: { days: false }
      });
    } catch (error) {
      console.error('Error fetching days:', error);
      this.setState({
        loading: { days: false },
        errors: { days: error.message }
      });
    }
  }

  async fetchPeriods() {
    try {
      this.setState({
        loading: { periods: true },
        errors: { periods: null }
      });

      const periods = await this.api.get('/period');

      this.setState({
        periods: periods || [],
        loading: { periods: false }
      });
    } catch (error) {
      console.error('Error fetching periods:', error);
      this.setState({
        loading: { periods: false },
        errors: { periods: error.message }
      });
    }
  }

  async fetchClasses(params = {}) {
    try {
      const requestedSchoolId = this.activeSchoolId;
      this.setState({
        loading: { classes: true },
        errors: { classes: null }
      });

      const query = new URLSearchParams();

      if (params.show_class_teacher !== undefined)
        query.set('show_class_teacher', params.show_class_teacher);
      if (params.is_active !== undefined)
        query.set('is_active', params.is_active);
      if (params.view_all !== undefined)
        query.set('view_all', params.view_all);
      if (params.non_assign_class !== undefined)
        query.set('non_assign_class', params.non_assign_class);

      const queryString = query.toString();
      const url = queryString ? `/class/all?${queryString}` : '/class/all';
      const classes = await this.api.get(url);

      if (this.activeSchoolId !== requestedSchoolId) return;

      this.setState({
        classes: classes || [],
        schoolId: requestedSchoolId,
        loading: { classes: false }
      });
    } catch (error) {
      console.error('Error fetching classes:', error);
      this.setState({
        loading: { classes: false },
        errors: { classes: error.message }
      });
    }
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  getDays() {
    return this.state.days || [];
  }

  getPeriods() {
    return this.state.periods || [];
  }

  getClasses() {
    return this.state.classes || [];
  }

  isLoading(key) {
    return this.state.loading[key] || false;
  }

  getError(key) {
    return this.state.errors[key] || null;
  }

  isAnyLoading() {
    return Object.values(this.state.loading).some(Boolean);
  }

  isReady() {
    return (
      this.state.initialized &&
      this.state.days.length > 0 &&
      this.state.periods.length > 0
    );
  }

  reset() {
    this.clearPersistedState();
    this.activeSchoolId = null;
    this.setState({
      ...this.defaultState,
      loading: { ...this.defaultState.loading },
      errors: { ...this.defaultState.errors },
    });
  }

  setActiveSchoolId(schoolId) {
    if (this.activeSchoolId === schoolId) return;
    this.activeSchoolId = schoolId;
    this.clearPersistedState();
    this.setState({
      ...this.defaultState,
      schoolId,
      loading: { ...this.defaultState.loading },
      errors: { ...this.defaultState.errors },
    });
  }

  getCurrentSchoolId() {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.schoolId ?? null;
    } catch {
      return null;
    }
  }
}

export default AppStateManager;